import { ChevronLeft, ChevronRight, Sparkles, WalletMinimal } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { formatDayHeading, formatMonth, monthKey, monthRange, shiftMonth, toISODate } from '@core/dates';

import { BarChart, CategoryBars } from '@/components/charts';
import { useEditor } from '@/components/ExpenseEditor';
import { ExpenseItem } from '@/components/ExpenseItem';
import { Button, Delta, EmptyState, IconButton } from '@/components/ui';
import { categoryTotals, monthOverMonth, summarize, trend } from '@/lib/analytics';
import { useApp, useMoney } from '@/lib/hooks';
import { liveExpenses } from '@/lib/merge';

function greeting(d: Date) {
  const h = d.getHours();
  return h < 5 || h >= 17 ? 'Good evening' : h < 12 ? 'Good morning' : 'Good afternoon';
}

export function Dashboard() {
  const { data, user, loaded } = useApp();
  const { format, formatWhole } = useMoney();
  const editor = useEditor();
  const navigate = useNavigate();
  const now = new Date();
  const current = monthKey(now);
  const [month, setMonth] = useState(current);
  const isCurrent = month === current;
  const today = toISODate(now);

  const view = useMemo(() => {
    const expenses = liveExpenses(data);
    const full = monthRange(month);
    const range = isCurrent ? { from: full.from, to: today } : full;
    const s = summarize(expenses, range);
    const recent = [...expenses]
      .sort((a, b) => b.expenseDate.localeCompare(a.expenseDate) || (b.expenseTime ?? '').localeCompare(a.expenseTime ?? '') || b.createdAt.localeCompare(a.createdAt))
      .slice(0, 6);
    return {
      any: expenses.length > 0,
      range,
      s,
      todayMinor: summarize(expenses, { from: today, to: today }).totalMinor,
      change: monthOverMonth(expenses, month, isCurrent ? now.getDate() : null),
      points: trend(expenses, range, 'day'),
      cats: categoryTotals(expenses, data.categories, range),
      recent,
    };
  }, [data, month, isCurrent, today]); // eslint-disable-line react-hooks/exhaustive-deps

  const openList = (params: Record<string, string>) => navigate(`/expenses?${new URLSearchParams(params)}`);

  if (!loaded) {
    return (
      <div className="page">
        <div className="skeleton" style={{ height: 32, width: 260, marginBottom: 20 }} />
        <div className="skeleton" style={{ height: 210, marginBottom: 16 }} />
        <div className="skeleton" style={{ height: 260 }} />
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-head">
        <h1>
          {greeting(now)}
          {user?.firstName ? `, ${user.firstName}` : ''}
        </h1>
        <div className="row">
          <IconButton icon={ChevronLeft} label="Previous month" onClick={() => setMonth((m) => shiftMonth(m, -1))} />
          <strong style={{ minWidth: 130, textAlign: 'center' }}>{formatMonth(month)}</strong>
          <IconButton icon={ChevronRight} label="Next month" disabled={isCurrent} onClick={() => setMonth((m) => shiftMonth(m, 1))} />
          {!isCurrent && (
            <Button variant="ghost" onClick={() => setMonth(current)}>
              This month
            </Button>
          )}
        </div>
      </div>

      {!view.any ? (
        <div className="card">
          <EmptyState
            icon={WalletMinimal}
            title="Let's start tracking your spending"
            text="Add your first expense here or in the Spendly app — it syncs both ways."
            action={<Button onClick={() => editor.open()}>Add expense</Button>}
          />
        </div>
      ) : (
        <div className="grid">
          <div className="grid grid-2">
            <section className="card hero">
              <div className="muted">{isCurrent ? 'Spent this month' : `Spent in ${formatMonth(month)}`}</div>
              <div className="hero-amount num">{format(view.s.totalMinor)}</div>
              {view.change !== null && <Delta percent={view.change} suffix={isCurrent ? 'same days last month' : 'the month before'} />}
              <div className="metrics">
                <div className="metric">
                  <div className="label">{isCurrent ? 'Today' : 'Largest'}</div>
                  <div className="value num">{format(isCurrent ? view.todayMinor : view.s.largest?.amountMinor ?? 0)}</div>
                </div>
                <div className="metric">
                  <div className="label">Avg / day</div>
                  <div className="value num">{formatWhole(view.s.avgPerDayMinor)}</div>
                </div>
                <div className="metric">
                  <div className="label">Expenses</div>
                  <div className="value num">{view.s.count}</div>
                </div>
              </div>
              {view.cats[0] && view.s.totalMinor > 0 && (
                <div className="row" style={{ marginTop: 16, background: 'var(--primary-soft)', color: 'var(--primary)', padding: '8px 12px', borderRadius: 12, fontSize: 14 }}>
                  <Sparkles size={16} />
                  {view.cats[0].category.name} is your top category at {Math.round((view.cats[0].totalMinor / view.s.totalMinor) * 100)}% of spending
                </div>
              )}
            </section>

            <section className="card">
              <div className="card-head">
                <h2>Top categories</h2>
                <Button variant="ghost" onClick={() => navigate('/insights')}>
                  Insights
                </Button>
              </div>
              {view.cats.length ? (
                <CategoryBars
                  items={view.cats.slice(0, 5).map((c) => ({ category: c.category, value: c.totalMinor }))}
                  total={view.s.totalMinor}
                  format={(v) => format(v)}
                  onSelect={(c) => openList({ categoryId: c.id, from: view.range.from, to: view.range.to })}
                />
              ) : (
                <p className="muted">No expenses in {formatMonth(month)}.</p>
              )}
            </section>
          </div>

          <section className="card">
            <div className="card-head">
              <h2>Daily spending</h2>
            </div>
            <BarChart
              key={month}
              data={view.points.map((p) => ({ key: p.key, value: p.totalMinor, label: String(Number(p.key.slice(8))), tooltip: formatDayHeading(p.key, now) }))}
              format={(v) => format(v)}
              formatAxis={(v) => format(v, true)}
              highlight={isCurrent ? today : undefined}
              onSelect={(b) => openList({ from: b.key, to: b.key })}
              label={`Daily spending for ${formatMonth(month)}`}
            />
          </section>

          <section className="card">
            <div className="card-head">
              <h2>Recent</h2>
              <Button variant="ghost" onClick={() => navigate('/expenses')}>
                See all
              </Button>
            </div>
            {view.recent.map((e) => (
              <ExpenseItem key={e.id} expense={e} category={data.categories.get(e.categoryId)} hasReceipt={data.receipts.has(e.id)} showDate onOpen={editor.open} />
            ))}
          </section>
        </div>
      )}
    </div>
  );
}
